/**
 * Sales Dashboard API client.
 *
 * Calls /api/admin/sales/* — read-only endpoints for the Sales dashboard.
 * Gated server-side by the `sales.view` permission so non-admin reps can use
 * these without the Admin Console permission.
 */

import { apiRequest } from './api';

const BASE = '/api/admin/sales';

export interface RfpInFlight {
  id: string;
  advertiser: string;
  agency?: string;
  budget?: string;
  budgetNumeric?: number;
  dueDate?: string;
  campaignStart?: string;
  campaignEnd?: string;
  emailFrom: string;
  emailDate: string;
  driveFolderUrl?: string;
  status: string;
  daysSinceReceived: number;
  classification?: string;
}

export interface FollowUpQueueSummary {
  lastRunAt: string | null;
  // 'skipped' = scanner was disabled at fire time; 'error' = run history couldn't be loaded
  lastRunStatus: 'running' | 'completed' | 'failed' | 'skipped' | 'error' | null;
  typeACount: number;
  typeBCount: number;
  typeDCount: number;
  totalThreadsScanned: number;
  daysSinceLastRun: number | null;
}

export interface ActivityEvent {
  id: string;
  timestamp: string;
  kind: 'rfp_received' | 'rfp_update' | 'follow_up_scan';
  title: string;
  detail: string;
  link?: string;
}

export interface SalesOverviewResponse {
  rfpsInFlight: RfpInFlight[];
  rfpStats: {
    total: number;
    last7Days: number;
    last30Days: number;
    distinctAdvertisers: number;
  };
  followUpQueue: FollowUpQueueSummary;
  recentActivity: ActivityEvent[];
  generatedAt: string;
}

export interface IoDeliveryItem {
  lineItemId: string;
  name: string;
  orderId?: string;
  orderName?: string;
  advertiserName?: string;
  startDate?: string;
  endDate?: string;
  status: string;
  phase: 'delivering' | 'launching_soon';
  expectedPercent?: number;
  percentDelivered?: number;
  pacingDelta?: number;
  pacingStatus: 'on_pace' | 'under_pacing' | 'over_pacing' | 'unknown';
  impressionsDelivered?: number;
  impressionsGoal?: number;
  daysUntilEnd?: number;
  daysUntilStart?: number;
  /** Serving days left INCLUDING today (the end date is a full serving day). */
  daysRemaining?: number;
  gamUrl?: string;
}

export interface IoDeliveryResponse {
  items: IoDeliveryItem[];
  totals: {
    delivering: number;
    launchingSoon: number;
    onPace: number;
    underPacing: number;
    overPacing: number;
    endingSoon: number;
  };
  warnings: string[];
  generatedAt: string;
}

export async function getSalesOverview(): Promise<SalesOverviewResponse> {
  return apiRequest(`${BASE}/overview`);
}

export async function getSalesIoDelivery(): Promise<IoDeliveryResponse> {
  return apiRequest(`${BASE}/io-delivery`);
}
